import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createRoom,
  deleteRoom,
  listRooms,
  setRoomAllowedSubjectAreas,
  updateRoom,
} from "./rooms-api";
import { SCHEDULE_KEY } from "./use-schedules";
import type { RoomCreateRequest, RoomUpdateRequest } from "../types";

export const ROOMS_KEY = (scheduleId: string) =>
  ["schedules", scheduleId, "rooms"] as const;

export function useRooms(
  scheduleId: string,
  options: { enabled?: boolean } = {},
) {
  const enabledGate = options.enabled ?? true;
  return useQuery({
    queryKey: ROOMS_KEY(scheduleId),
    queryFn: () => listRooms(scheduleId),
    enabled: enabledGate && !!scheduleId,
  });
}

export function useCreateRoom(scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RoomCreateRequest) => createRoom(scheduleId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOMS_KEY(scheduleId) });
      // Counts (distinctRooms) may shift.
      qc.invalidateQueries({ queryKey: SCHEDULE_KEY(scheduleId) });
    },
  });
}

export function useUpdateRoom(scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roomId,
      payload,
    }: {
      roomId: string;
      payload: RoomUpdateRequest;
    }) => updateRoom(scheduleId, roomId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOMS_KEY(scheduleId) });
      qc.invalidateQueries({ queryKey: SCHEDULE_KEY(scheduleId) });
    },
  });
}

export function useDeleteRoom(scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roomId: string) => deleteRoom(scheduleId, roomId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOMS_KEY(scheduleId) });
      qc.invalidateQueries({ queryKey: SCHEDULE_KEY(scheduleId) });
    },
  });
}

/**
 * Replaces a room's allowed-subject-area restrictions in one shot. Empty list
 * clears restrictions (any subject area allowed). Only ROOMS_KEY needs
 * invalidation — counts are unaffected by membership changes.
 */
export function useSetRoomAllowedSubjectAreas(scheduleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roomId,
      subjectAreaIds,
    }: {
      roomId: string;
      subjectAreaIds: string[];
    }) => setRoomAllowedSubjectAreas(scheduleId, roomId, subjectAreaIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOMS_KEY(scheduleId) });
    },
  });
}
